import React from 'react';
import { push } from 'connected-react-router';
import { reduxForm, stopSubmit } from 'redux-form';
import { connect } from 'react-redux';
import { actionCreators } from 'react/api/actions/crud';
import PropTypes from 'prop-types';
import { fromJS } from 'immutable';

class BaseForm extends React.Component {
  componentDidMount() {
    const {
      id, entity, fetchInitialFormValuesAction, customCriteria, customCriteriaChain
    } = this.props;
    if (id) {
      fetchInitialFormValuesAction({
        id, entity, goToGrid: this.goToGrid, customCriteria, customCriteriaChain
      });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      submitFailed,
      valid,
      entity,
      saveSuccess,
      saveError,
      saveFormErrorAction,
      clearFormErrorAction,
      stopSubmitAction,
      errorMessage,
      invalidSubmitCustom
    } = this.props;

    if (saveSuccess) {
      this.goToGrid();
    }

    if (submitFailed && !valid) {
      if (prevProps.valid) {
        stopSubmitAction();
      } else {
        saveFormErrorAction({ entity, customError: 'general.form.submitError' });
      }
    }

    if (errorMessage && valid && !saveError && !submitFailed && !invalidSubmitCustom) {
      clearFormErrorAction({ entity });
    }
  }

  componentWillUnmount() {
    const { entity, keepDataOnUnmount, clearFormStateAction } = this.props;

    if (!keepDataOnUnmount) 
      clearFormStateAction({ entity });
  }

  goToGrid = () => {
    const { entity } = this.props;

    window.globalStore.dispatch(push(`/${entity}`));
  };

  handleSubmit = (values) => {
    const {
      id,
      entity,
      updateMethod,
      saveFormAction,
      customSubmitAction,
      beforeSubmit,
      stopSubmitAction,
      saveFormErrorAction,
      afterSubmit
    } = this.props;
    const finalFormValues = beforeSubmit(values, id);

    if (!finalFormValues) {
      stopSubmitAction();
      saveFormErrorAction({ entity, customError: 'general.form.submitError' });
    } else {
      const payload = {
        id,
        entity,
        updateMethod,
        data: finalFormValues,
        afterSubmit
      }
      if (customSubmitAction) {
        customSubmitAction(payload);
        return;
      }
      saveFormAction(payload);
    }
  }

  renderError = () => {
    const {
      submitFailed,
      valid,
      saving,
      errorMessage
    } = this.props;

    let className = '';
    className = errorMessage ? 'showError' : className;
    className = ((submitFailed && valid) || saving) ? 'hideError' : className;

    return (
      <div className={`formError ${className}`}>
        {Monet.I18n.getResourcesByKey(errorMessage)}
      </div>
    );
  };

  helpOnClick = () => {
    const { entity, tabName } = this.props;
    const help = new Monet.UI.Help(entity, Monet.I18n.getResourcesByKey(tabName));
    help.logIn();
  };

  renderHelpButton = () => (
    <div className="formHelpButton">
      <button className="MonetUIHelpLink" onClick={this.helpOnClick} type="button" aria-label="help" />
    </div>
  );

  renderCancelButton = (customLabel) => {
    const { saving, loadingInitialValues, customCloseAction } = this.props;
    const label = Monet.I18n.getResourcesByKey(customLabel || 'default.button.cancel');
    return (
      <input type="button" value={label} onClick={customCloseAction || this.goToGrid} disabled={(saving || loadingInitialValues)} className="btn-secondary" />
    );
  };

  renderSaveButton = (customLabel) => {
    const { saving, isLoading, handleSubmit, loadingInitialValues } = this.props;
    const isWorkingOnLoading = saving || isLoading || loadingInitialValues;

    const saveClassName = isWorkingOnLoading ? 'submitting' : '';
    const label = Monet.I18n.getResourcesByKey(customLabel || 'default.button.save');
    return (
      <input type="button" value={label} onClick={handleSubmit(this.handleSubmit)} disabled={isWorkingOnLoading} className={saveClassName} />
    );
  };

  renderFormButtons = ({ showHelp } = {}) => {
    const { saveButtonLabel, cancelButtonLabel } = this.props;

    return (
      <div className="formButtons">
        {this.renderCancelButton(cancelButtonLabel)}
        {this.renderSaveButton(saveButtonLabel)}
        { showHelp ? this.renderHelpButton() : null }
      </div>
    );
  }

  onKeyPress = (event) => {
    const elementFocusedWhenPressed = event.target;

    if (event.key === 'Enter' && elementFocusedWhenPressed.tagName !== 'BUTTON') {
      event.preventDefault();
    }
  }

  render() {
    const { children } = this.props;

    return (
      <form className="generalForm" onKeyPress={this.onKeyPress}>
        {this.renderError()}
        {this.renderFormButtons({ showHelp: true })}
        {children}
        {this.renderFormButtons()}
      </form>
    );
  }
}

BaseForm.propTypes = {
  children: PropTypes.node.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  saveFormAction: PropTypes.func.isRequired,
  saveFormErrorAction: PropTypes.func.isRequired,
  clearFormErrorAction: PropTypes.func.isRequired,
  clearFormStateAction: PropTypes.func.isRequired,
  fetchInitialFormValuesAction: PropTypes.func.isRequired,
  entity: PropTypes.string.isRequired,
  tabName: PropTypes.string.isRequired,
  id: PropTypes.string,
  updateMethod: PropTypes.string,
  submitFailed: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  saveSuccess: PropTypes.bool.isRequired,
  saveError: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  beforeSubmit: PropTypes.func,
  customCriteria: PropTypes.array,
  stopSubmitAction: PropTypes.func.isRequired,
  loadingInitialValues: PropTypes.bool.isRequired,
  invalidSubmitCustom: PropTypes.bool.isRequired,
  customCriteriaChain: PropTypes.object,
  keepDataOnUnmount: PropTypes.bool
};

BaseForm.defaultProps = {
  id: null,
  updateMethod: 'PUT',
  errorMessage: '',
  beforeSubmit: (values) => values,
  customCriteria: [],
  customCriteriaChain: null,
  isLoading: null,
  keepDataOnUnmount: false
};

const form = reduxForm({
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  destroyOnUnmount: false
})(BaseForm);

const mapStateToProps = (state, ownProps) => ({
  initialValues: state.crud.getIn([ownProps.entity, 'initialValues'], fromJS({})).toJSON(),
  saving: state.crud.getIn([ownProps.entity, 'saving'], false),
  saveSuccess: state.crud.getIn([ownProps.entity, 'saveSuccess'], false),
  saveError: state.crud.getIn([ownProps.entity, 'saveError'], false),
  errorMessage: state.crud.getIn([ownProps.entity, 'errorMessage'], ''),
  invalidSubmitCustom: state.crud.getIn([ownProps.entity, 'invalidSubmitCustom'], false),
  loadingInitialValues: state.crud.getIn([ownProps.entity, 'loadingInitialValues'], false),
  form: ownProps.entity
});

const mapDispatchToProps = {
  fetchInitialFormValuesAction: actionCreators.fetchInitialFormValues,
  saveFormAction: actionCreators.saveForm,
  saveFormErrorAction: actionCreators.saveFormError,
  clearFormErrorAction: actionCreators.clearFormError,
  clearFormStateAction: actionCreators.clearFormState,
  stopSubmitAction: stopSubmit
};

export default connect(mapStateToProps, mapDispatchToProps)(form);
